<script lang="ts">
  import axios from 'axios';
  import { onMount } from 'svelte';
  import { niveauxMaturite } from '../niveaux-maturite/NiveauxMaturite.donnees';
  import { récupèreStatistiquesMSC, type Statistiques } from '../passerelles/statistiquesMSC';
  import GraphiqueAnneau from '../test-maturite/GraphiqueAnneau.svelte';
  import LegendeAnneau from '../test-maturite/LegendeAnneau.svelte';
  import type { RepartitionResultatsTestPourUnNiveau } from '../test-maturite/ResultatsTest.type';
  import { construisSerie } from '../test-maturite/resultatTest';
  import SelectRegion from '../test-maturite/SelectRegion.svelte';
  import SelectSecteurActivite from '../test-maturite/SelectSecteurActivite.svelte';
  import SelectTailleOrganisation from '../test-maturite/SelectTailleOrganisation.svelte';
  import type { Serie } from '../test-maturite/Serie';
  import Bouton from '../ui/Bouton.svelte';
  import Heros from '../ui/Heros.svelte';
  import IllustrationDragonPasDeResultat from '../ui/IllustrationDragonPasDeResultat.svelte';
  import Tuile from './Tuile.svelte';

  let statistiques: Statistiques | undefined = $state();
  let serieNonFiltree: Serie = $state([]);
  let serie: Serie = $state([]);

  let region = $state('');
  let secteur = $state('');
  let tailleOrganisation = $state('');
  const filtreActif = $derived(!!secteur || !!tailleOrganisation || !!region);

  $effect(() => {
    void chargeRepartitionsDesResultats({
      secteur,
      region,
      tailleOrganisation,
    });
  });

  async function chargeRepartitionsDesResultats({
    region,
    secteur,
    tailleOrganisation,
  }: {
    secteur: string;
    tailleOrganisation: string;
    region: string;
  }) {
    if (!filtreActif) {
      serie = serieNonFiltree;
      return;
    }
    const parametres = new URLSearchParams({
      secteur,
      tailleOrganisation,
      region,
    });
    const reponse = await axios.get<RepartitionResultatsTestPourUnNiveau[]>(
      '/api/repartition-resultats-test?' + parametres.toString()
    );
    if (reponse.status === 204) {
      serie = [];
      return;
    }

    const repartitions = reponse.data;

    serie = construisSerie({
      repartitions,
      mode: 'absolu',
    });
  }

  const reinitialiseLesFiltres = async () => {
    region = '';
    secteur = '';
    tailleOrganisation = '';
  };

  onMount(async () => {
    statistiques = await récupèreStatistiquesMSC();

    for (const [idNiveau, valeur] of Object.entries(statistiques.testsMaturite.parNiveau)) {
      const libelle = niveauxMaturite.find((niveau) => niveau.id === idNiveau)!.label;
      serieNonFiltree.push({ libelle, valeur });
    }
  });
</script>

<Heros
  description="L'impact de MesServicesCyber en quelques chiffres"
  format="banniere"
  theme="clair"
  titre="Nos statistiques"
/>

<dsfr-container class="statistiques">
  {#if statistiques}
    <div class="tuiles">
      <Tuile
        description="Utilisateurs inscrits"
        image="stat-utilisateurs-inscrits"
        mesure={statistiques.utilisateursInscrits}
      />
      <Tuile description="Tests cyber" image="stat-test-cyber" mesure={statistiques.testsRéalisés} />
      <Tuile
        description="Démarches de sécurisation"
        image="stat-demarches-de-securisation"
        mesure={statistiques.démarchesDeSécurisation}
      />
    </div>
    <div class="repartition">
      <div>
        <h2>Baromètre de la maturité cyber</h2>
        <span class="description"
          >Répartition de la maturité cyber des organisations sur l’ensemble des tests réalisés.</span
        >
      </div>
      <div class="filtres">
        <p>Affinez les résultats en appliquant des filtres par secteur, région ou taille d’organisation.</p>

        <div class="champs">
          <div class="champ-filtre">
            <SelectSecteurActivite
              bind:secteur
              libelle="Secteur d'activité"
              optionDefautIntitule="Tous les secteurs"
              optionDefautSelectionnable
            />
          </div>

          <div class="champ-filtre">
            <SelectRegion
              bind:region
              libelle="Région"
              optionDefautIntitule="Toutes les régions"
              optionDefautSelectionnable
            />
          </div>

          <div class="champ-filtre">
            <SelectTailleOrganisation
              libelle="Taille de l'organisation"
              bind:tailleOrganisation
              optionDefautIntitule="Toutes les tailles d'organisation"
              optionDefautSelectionnable
            />
          </div>
        </div>
      </div>
      {#if serie.length > 0}
        <div class="donnees-graphiques">
          <GraphiqueAnneau {serie} nomDeLaDonnee="tests réalisés" />
          <LegendeAnneau {serie} />
        </div>
      {:else}
        <section class="pas-assez-de-resultats">
          <div class="contenu-section">
            <IllustrationDragonPasDeResultat texteAlternatif="Pas assez de résultats" petit />
            <h4>Pas de résultat 😔</h4>
            <p>
              Nous n’avons pas encore assez de données pour afficher une comparaison fiable avec les filtres
              sélectionnés.
            </p>
            <Bouton libelle="Réinitialiser les filtres" type="tertiaire" taille="md" surClic={reinitialiseLesFiltres} />
          </div>
        </section>
      {/if}
    </div>
  {/if}
</dsfr-container>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  .tuiles {
    display: grid;
    gap: 24px;
    @include a-partir-de(sm) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  .repartition {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    padding: 1.5rem;
    border: 1px solid var(--border-default-grey);
    border-radius: 8px;
    gap: 2rem;

    @include a-partir-de(sm) {
      padding: 2rem;
    }

    h2 {
      font-size: 1.5rem;
      line-height: 2rem;
      font-weight: bold;
      margin: 0;
      @include a-partir-de(sm) {
        font-size: 1.75rem;
        line-height: 2.25rem;
      }
    }

    .description {
      color: #3a3a3a;
    }

    .donnees-graphiques {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      align-items: center;
      align-self: stretch;
      gap: 48px;
      padding: 3rem 0;
      @include a-partir-de(sm) {
        align-items: center;
        justify-content: space-around;
        flex-direction: row;
      }
    }

    .filtres {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .champs {
        display: grid;
        gap: 1.5rem;
        grid-template-columns: 1fr;
        @include a-partir-de(sm) {
          grid-template-columns: repeat(2, 1fr);
        }
        @include a-partir-de(md) {
          grid-template-columns: repeat(3, 1fr);
        }

        .champ-filtre {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          flex: 1;
        }
      }

      p {
        color: #161616;
        font-size: 1.125rem;
        font-weight: bold;
        line-height: 1.75rem;
        margin: 0;
      }
    }

    .pas-assez-de-resultats {
      margin-bottom: 3rem;
      .contenu-section {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        text-align: center;
      }

      h4 {
        font-size: 1.375rem;
        line-height: 1.75rem;
        max-width: 588px;
        margin: 0;
        @include a-partir-de(sm) {
          font-size: 1.5rem;
          line-height: 2rem;
        }
      }

      p {
        font-size: 1.125rem;
        line-height: 1.75rem;
        max-width: 588px;
        margin: 0;
      }
    }
  }
</style>
